import { RefObject } from 'react';
import { TextLink } from '@dsg-tech/homefield-react';
import { checkStores, setDataEmTag } from 'utility/utility';
import { KEY_CODES } from 'utility/constants';
import './FlexL1Columns.scss';

export interface L1CategoryData {
  title?: string;
  link?: string;
  l2Categories: {
    title: string;
    link: string;
  }[];
}

const createSkipLink = (l1Categories: L1CategoryData[], currentColumnIndex: number) => {
  if (l1Categories[currentColumnIndex + 1]) {
    return l1Categories[currentColumnIndex + 1].title;
  }
  return '';
};

const FlexL1Columns = ({
  l1Categories,
  l0Title,
  changeFocus,
  firstSubLinkRef,
}: {
  l0Title: string;
  l1Categories: L1CategoryData[];
  changeFocus: (offset?: number) => void;
  firstSubLinkRef: RefObject<HTMLAnchorElement | null>;
}) => {
  const handleColumnCategoryKeyDown = (
    event: React.KeyboardEvent,
    columnCategoryIndex: number,
  ): void => {
    if (event.key === KEY_CODES.ESCAPE) {
      changeFocus();
    }
    // shift + tab
    else if (event.shiftKey && event.key === KEY_CODES.TAB) {
      if (columnCategoryIndex === 0) {
        changeFocus(1);
      }
    }
  };

  return (
    <div
      className="hmf-w-100 hmf-h-100 hmf-display-flex hmf-flex-column hmf-flex-wrap"
      tabIndex={-1}>
      {l1Categories.map((l1ColumnCategory, columnCategoryIndex) => {
        const { title: l1CategoryName, link: l1CategoryLink, l2Categories } = l1ColumnCategory;
        const categorySkipLink = createSkipLink(l1Categories, columnCategoryIndex);
        return (
          <div className="hmf-mb-m hmf-pl-m" key={`${l1CategoryName}-list`}>
            {l1CategoryName && l1CategoryLink && (
              <TextLink
                variant="group"
                id={l1CategoryName}
                href={checkStores(l1CategoryLink)}
                data-em={setDataEmTag('TopNav', l0Title, l1CategoryName)}
                ref={columnCategoryIndex === 0 ? firstSubLinkRef : null}
                onKeyDown={(event) => handleColumnCategoryKeyDown(event, columnCategoryIndex)}>
                <h2 className="hmf-m-0 hmf-header-bold-s">{l1CategoryName}</h2>
              </TextLink>
            )}
            <ul className="dynamic-category-list hmf-p-0">
              {/* Skip Link */}
              <li className="hmf-mb-xs">
                <TextLink
                  className="skip-link"
                  variant="group"
                  href={`#${categorySkipLink}`}
                  onKeyDown={(event) => {
                    if (event.key === KEY_CODES.ESCAPE) {
                      changeFocus();
                    } else if (event.key === KEY_CODES.SPACE || event.key === KEY_CODES.ENTER) {
                      event.preventDefault();
                      window.location.assign(`/#${categorySkipLink}`);
                    }
                  }}>
                  {`Skip to ${categorySkipLink}`}
                </TextLink>
              </li>
              {/* Remaining l2 category links */}
              {l2Categories.map((l2Category) => {
                const l2CategoryName = l2Category.title;
                const l2CategoryLink = l2Category.link;
                return (
                  <li
                    key={`${l2CategoryLink}_${l2CategoryName}`}
                    className="hmf-subheader-m hmf-mb-xs">
                    <TextLink
                      className={l1CategoryName ? '' : 'hmf-body-bold-m'}
                      variant="group"
                      href={checkStores(l2CategoryLink)}
                      data-em={
                        l1CategoryName
                          ? setDataEmTag('TopNav', l0Title, l1CategoryName, l2CategoryName)
                          : setDataEmTag('TopNav', l0Title, l2CategoryName)
                      }
                      onKeyDown={(event) => {
                        if (event.key === KEY_CODES.ESCAPE) {
                          changeFocus();
                        }
                      }}>
                      {l2CategoryName}
                    </TextLink>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

export default FlexL1Columns;
